import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PaperPlaneTilt, FileText, EnvelopeSimple } from "@phosphor-icons/react";
import { RecipientPicker } from "@/components/RecipientPicker";

/**
 * Dialog "Richiedi integrazione documenti".
 *
 * L'operatore/admin scrive il testo dei documenti mancanti; alla conferma il
 * backend:
 *   1. Invia una email al professionista (via Resend) con Reply-To in
 *      plus-address che punta al webhook inbound.
 *   2. Aggiunge un record in `practice.integration_requests` con status="pending".
 *   3. Traccia in chat pratica un messaggio di sistema.
 *
 * Quando il pro risponde all'email allegando i documenti, il webhook
 * `/api/webhooks/inbound-email` salva gli allegati nella cartella
 * "Documenti ricevuti via email" dell'immobile e notifica gli operatori.
 */
export function RequestDocumentsDialog({
  open, onClose, practiceId, professionalEmail, clientEmail, serviceName, onSent,
}) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sendTo, setSendTo] = useState("pro");
  const toClient = sendTo === "client";
  const destEmail = toClient ? clientEmail : professionalEmail;
  const destLabel = toClient ? "cliente finale" : "professionista";
  const suggestions = [
    "Visura catastale aggiornata dell'immobile",
    "Planimetria catastale in scala 1:200",
    "Atto di provenienza (rogito, successione, donazione)",
    "Copia documento di identità del proprietario",
    "Titolo edilizio esistente (permesso di costruire / SCIA / DIA)",
    "APE precedente (se disponibile)",
  ];

  useEffect(() => {
    if (!open) { setMessage(""); setSendTo("pro"); }
  }, [open]);

  const addSuggestion = (s) => {
    setMessage((m) => {
      const line = `• ${s}`;
      if (!m || !m.trim()) return line;
      if (m.includes(s)) return m;
      return m.trimEnd() + "\n" + line;
    });
  };

  const submit = async () => {
    const text = message.trim();
    if (!text) { toast.error(`Scrivi cosa serve al ${destLabel}`); return; }
    setSending(true);
    try {
      const r = await api.post(`/practices/${practiceId}/request-documents`, { message: text, send_to: sendTo });
      if (r.data?.email_sent) {
        toast.success(`Email inviata a ${r.data?.email_to || destEmail} (${destLabel}). Attendi i documenti.`);
      } else if (r.data?.email_error) {
        toast.warning(`Richiesta salvata, ma l'email non è partita: ${r.data.email_error}`);
      } else {
        toast.success("Richiesta salvata.");
      }
      onSent && onSent(r.data);
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!sending && !v) onClose && onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="request-documents-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={18} weight="bold" /> Richiedi integrazione documenti
          </DialogTitle>
          <DialogDescription className="text-left">
            Scrivi i documenti mancanti. Verrà inviata un'email a{" "}
            <strong className="text-slate-800">{destEmail || "—"}</strong> ({destLabel})
            {serviceName && <> per la pratica <strong className="text-slate-800">{serviceName}</strong></>}.
            <br />
            {toClient
              ? "Il cliente riceverà un link sicuro per caricare i file, attivo finché la pratica è aperta."
              : "Il professionista caricherà i file dalla propria area: i documenti finiranno nella cartella dell'immobile."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <RecipientPicker value={sendTo} onChange={setSendTo} clientEmail={clientEmail} testIdPrefix="docreq-send-to" />
          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">
              Suggerimenti rapidi (clicca per aggiungere)
            </label>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => addSuggestion(s)}
                  className="text-[11px] px-2 py-1 rounded-full border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-800 font-medium transition"
                  data-testid={`docreq-suggest-${s.substring(0, 12).replace(/\s+/g, "-")}`}
                >
                  + {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">
              Messaggio per il {destLabel}
            </label>
            <Textarea
              rows={7}
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 5000))}
              maxLength={5000}
              placeholder={"Elenca i documenti mancanti, uno per riga.\n\nEsempio:\n• Visura catastale aggiornata\n• Planimetria in scala 1:200\n• Ultimo APE"}
              className="text-sm"
              data-testid="docreq-message-input"
            />
            <div className="text-[10px] text-slate-500 mt-1">
              {message.length}/5000 caratteri
            </div>
          </div>

          {!toClient && !professionalEmail && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800" data-testid="docreq-no-pro-email">
              ⚠️ Email del professionista non presente sulla pratica: il server proverà a usare quella del suo profilo.
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onClose && onClose()}
            disabled={sending}
            data-testid="docreq-cancel"
          >
            Annulla
          </Button>
          <Button
            className="ht-gradient border-0 text-white"
            onClick={submit}
            disabled={sending || !message.trim() || (toClient && !clientEmail)}
            data-testid="docreq-submit"
          >
            {sending ? (
              <>Invio…</>
            ) : (
              <><EnvelopeSimple size={14} className="mr-2" weight="bold" /> Invia richiesta</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RequestDocumentsDialog;
